import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { toast } from 'sonner';

import { SharedCardBack } from '@/components/practice/SharedCardBack';
import { PracticeShell } from '@/components/practice/PracticeShell';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import {
  CEFR_LEVELS,
  type CefrLevel,
  type Card,
  type CoverageTier,
  type NounsPayload,
  type SessionMode,
  type SessionMiss,
  type SessionStartResponse,
} from '@/core/types';
import { api, ApiError } from '@/lib/api';
import { getAppDefinition, grammarCourseFor, isGrammarCourse } from '@/apps';
import { useAuxLanguages } from '@/lib/queries';
import { GrammarLessonScreen } from '@/apps/shared/grammar/LessonScreen';
import type { GrammatikSession } from '@/apps/grammatik/types';
import type { AnswerEvent } from '@/apps/types';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyCard = Card<any>;

function parseLevelsParam(raw: string | null): CefrLevel[] | undefined {
  if (!raw) return undefined;
  const allowed = new Set<string>(CEFR_LEVELS);
  const parts = raw
    .split(',')
    .map((s) => s.trim().toUpperCase())
    .filter((s) => allowed.has(s));
  return parts.length > 0 ? (parts as CefrLevel[]) : undefined;
}

/** Coverage-tier filter for catalog vocabulary apps. Sibling of `levels`. */
function parseTiersParam(raw: string | null): CoverageTier[] | undefined {
  if (!raw) return undefined;
  const parts = raw
    .split(',')
    .map((s) => Number.parseInt(s.trim(), 10))
    .filter((n): n is CoverageTier => n === 1 || n === 2 || n === 3);
  return parts.length > 0 ? parts : undefined;
}

type AnswerState = { correct: boolean; user_answer: string | null; detail?: unknown };

export function PracticeRoute() {
  const { appId } = useParams<{ appId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const mode = (searchParams.get('mode') as SessionMode) ?? 'practice';
  const levels = parseLevelsParam(searchParams.get('levels'));
  const tiers = parseTiersParam(searchParams.get('tiers'));

  // The user's explanation languages, not a hardcoded pair.
  //
  // These have been settable since the first release and honoured nowhere: the
  // Languages section wrote them to the database and every card face received
  // the literals "en" and "pt". Invisible while one Brazilian used the app, and
  // exactly wrong the moment a German joined and needed German and English.
  const { primaryLang, secondaryLang } = useAuxLanguages();

  const [session, setSession] = useState<SessionStartResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [cardIdx, setCardIdx] = useState(0);
  const [showBack, setShowBack] = useState(false);
  const [lastAnswer, setLastAnswer] = useState<AnswerState | null>(null);
  // Every wrong answer, in the order they happened, for the results screen's
  // "what you missed" list. Kept here rather than fetched back after finishing:
  // the shell already holds both the card and the answer, so a round trip would
  // buy nothing but a schema.
  const [misses, setMisses] = useState<SessionMiss[]>([]);
  const [finishing, setFinishing] = useState(false);
  const [quitOpen, setQuitOpen] = useState(false);

  /**
   * Grammatik's daily lesson, and whether it has been read.
   *
   * The reading screen sits in front of the cards rather than being one of
   * them: it records no attempt, so it cannot be a card without the engine
   * scheduling and grading something there is no answer to. Every other app
   * leaves both of these null and is unaffected.
   */
  const [lesson, setLesson] = useState<GrammatikSession['lesson']>(null);
  const [isReviewLesson, setIsReviewLesson] = useState(false);
  const [lessonRead, setLessonRead] = useState(false);

  // Start session on mount.
  useEffect(() => {
    if (!appId) return;
    let cancelled = false;

    // A grammar course builds its deck in a fixed order (three from yesterday,
    // six from today, one or two spaced) rather than by weighted sampling, so
    // it starts through its own endpoint. Everything after this point —
    // attempts, finishing, the streak — is the shared path.
    // A single-lesson revision run, started from the review screen. Same deck
    // shape, different endpoint, and NOT streak-eligible — see the route.
    const lessonParam = Number(searchParams.get('lesson'));
    const redoLesson =
      mode === 'lesson' && Number.isInteger(lessonParam) && lessonParam > 0 ? lessonParam : null;

    // A topic run draws from every finished lesson in one curriculum block. No
    // lesson screen precedes it: a topic spans several rules and there is no
    // single one to read first.
    const blockParam = Number(searchParams.get('block'));
    const topicBlock =
      mode === 'topic' && Number.isInteger(blockParam) && blockParam > 0 ? blockParam : null;

    const grammarStart = () => {
      if (topicBlock !== null) return api.grammarPracticeTopic(appId, topicBlock);
      if (redoLesson !== null) return api.grammarPracticeLesson(appId, redoLesson);
      return api.grammarStartSession(appId);
    };

    const start = isGrammarCourse(appId)
      ? grammarStart().then((s) => {
          if (!cancelled) {
            setLesson(s.lesson);
            setIsReviewLesson(s.is_review_lesson);
          }
          return {
            session_id: s.session_id,
            mode: s.mode,
            cards: s.cards,
          } as SessionStartResponse;
        })
      : api.startSession({
          app_id: appId,
          mode,
          ...(levels ? { levels } : {}),
          ...(tiers ? { tiers } : {}),
        });

    start
      .then((s) => {
        if (!cancelled) setSession(s);
      })
      .catch((err: ApiError) => {
        if (!cancelled) setError(err.message);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (error) {
    return (
      <PracticeShell mode={mode} onQuit={() => navigate(`/apps/${appId}`)} current={0} total={0}>
        <div className="text-destructive text-center">{error}</div>
      </PracticeShell>
    );
  }

  if (!session) {
    return (
      <PracticeShell mode={mode} onQuit={() => navigate(`/apps/${appId}`)} current={0} total={0}>
        <div className="text-muted-foreground text-center">Loading session…</div>
      </PracticeShell>
    );
  }

  // The lesson comes first, outside the practice shell: it is a page to read,
  // not a card to answer, and the shell's "3 / 10" counter would be a lie.
  const grammarBinding = grammarCourseFor(appId);
  if (lesson && !lessonRead && grammarBinding) {
    return (
      <main className="mx-auto w-full max-w-2xl px-4 py-6">
        <GrammarLessonScreen
          lesson={grammarBinding.toLessonView(lesson)}
          course={grammarBinding.course}
          primaryLang={primaryLang}
          isReviewLesson={isReviewLesson}
          onStart={() => setLessonRead(true)}
        />
      </main>
    );
  }

  const total = session.cards.length;
  const currentCard = session.cards[cardIdx];
  const isLastCard = cardIdx === total - 1;

  const appDef = getAppDefinition(appId!);
  if (!appDef) {
    return (
      <PracticeShell mode={mode} onQuit={() => navigate(`/apps/${appId}`)} current={0} total={0}>
        <div className="text-destructive text-center">Unknown app: {appId}</div>
      </PracticeShell>
    );
  }

  const handleAnswer = (event: AnswerEvent) => {
    // "I already know this" is an assertion, not an answer. Mark the card
    // memorized, log NO attempt — otherwise accuracy fills up with answers the
    // user never gave — and skip straight past the card back, since there is no
    // verdict to show. The session's card total shrinks with it, so a skipped
    // card can't drag the session accuracy down either.
    if (event.kind === 'asserted_known') {
      api
        .markCardKnown(currentCard.id, session.session_id)
        .catch((err) => console.error('Failed to mark card known:', err));
      void handleNext();
      return;
    }

    setLastAnswer({ correct: event.correct, user_answer: event.user_answer, detail: event.detail });

    if (!event.correct) {
      setMisses((prev) => [...prev, { card: currentCard, user_answer: event.user_answer }]);
    }

    api
      .recordAttempt(session.session_id, {
        card_id: currentCard.id,
        correct: event.correct,
        time_ms: event.time_ms,
        // Banked since migration 0006 — it is what lets the mistakes list say
        // *how* a card is being missed, not just how often.
        user_answer: event.user_answer,
        // Passed through without being read. Apps that ask more than one
        // question per card put the per-part result here; migration 0010.
        detail: event.detail,
      })
      .catch((err) => console.error('Failed to record attempt:', err));

    setShowBack(true);
  };

  const handleNext = async () => {
    if (!isLastCard) {
      setShowBack(false);
      setLastAnswer(null);
      setCardIdx((idx) => idx + 1);
      return;
    }
    setFinishing(true);
    try {
      const result = await api.finishSession(session.session_id);
      // Advance the curriculum, but only after the shared finish has decided
      // the session was completed — the server re-checks, so a quit halfway
      // through cannot skip a lesson.
      // Asked unconditionally, because the SERVER decides. `/advance` is
      // conditional on `streak_eligible`, which a revision run never has, so a
      // redo cannot move the curriculum however it was reached.
      //
      // This used to also test `mode !== 'lesson'`, and that was a real bug:
      // the mode came from the URL rather than from the session, so a DAILY
      // session started from a URL still carrying `mode=lesson` — which is
      // exactly what the results screen's "Practice again" produced — counted
      // for the streak and then silently failed to advance the curriculum. The
      // lesson was done, `grammar_progress` got no row, and the same lesson
      // came round again the next day.
      if (lesson && isGrammarCourse(appId)) {
        await api
          .grammarAdvance(appId, session.session_id)
          .catch((err: unknown) => console.error('Failed to advance the lesson:', err));
        await queryClient.invalidateQueries({ queryKey: [appId, 'state'] });
      }
      await queryClient.invalidateQueries({ queryKey: ['apps'] });
      await queryClient.invalidateQueries({ queryKey: ['streak', 'overall'] });
      await queryClient.invalidateQueries({ queryKey: ['stats', 'overall'] });
      // The session may have flagged or cleared cards, so both the mode button's
      // count (per-app stats) and the list itself are now stale.
      await queryClient.invalidateQueries({ queryKey: ['apps', appId] });
      navigate(`/apps/${appId}/results`, {
        // `ratedLesson` is what the results screen asks about. Null on a
        // post-curriculum review, where the lesson on screen is "the rule you
        // are weakest on" rather than one just studied — asking "how well did
        // that land?" about a rule the session never taught would collect an
        // opinion about the wrong thing.
        state: {
          result,
          mode,
          misses,
          /**
           * The query this session was started with, so "Practice again" can
           * repeat it rather than approximate it.
           *
           * It used to rebuild the URL from `mode` alone, which dropped
           * `lesson` and `block` — turning "practise lesson 12 again" into a
           * daily session wearing a revision label — and dropped `levels` and
           * `tiers` too, so "Practice again" after an A1-filtered vocabulary
           * session quietly handed back the whole deck.
           */
          search: searchParams.toString(),
          ratedLesson:
            lesson && !isReviewLesson && isGrammarCourse(appId)
              ? { n: lesson.n, title: lesson.title }
              : null,
        },
        replace: true,
      });
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : String(err);
      toast.error('Failed to finish session', { description: msg });
      setFinishing(false);
    }
  };

  // Apps may ship a CardBack override; otherwise use the shared noun-style
  // back. Cast to AnyCard at the boundary because the registry is generic and
  // each component re-narrows in its own implementation.
  const CardBackComponent = appDef.CardBack ?? SharedCardBack;

  return (
    <>
      <PracticeShell
        mode={mode}
        onQuit={() => setQuitOpen(true)}
        current={cardIdx + 1}
        total={total}
        struggling={currentCard.struggling ?? false}
      >
        {showBack && lastAnswer ? (
          <CardBackComponent
            card={currentCard as Card<NounsPayload> & AnyCard}
            correct={lastAnswer.correct}
            user_answer={lastAnswer.user_answer}
            answer_detail={lastAnswer.detail}
            primary_lang={primaryLang}
            secondary_lang={secondaryLang}
            isLastCard={isLastCard}
            onNext={handleNext}
          />
        ) : (
          <appDef.CardFront card={currentCard as AnyCard} onAnswer={handleAnswer} />
        )}
        {finishing && (
          <p className="text-muted-foreground mt-4 text-center text-sm">Saving session…</p>
        )}
      </PracticeShell>

      <ConfirmDialog
        open={quitOpen}
        onOpenChange={setQuitOpen}
        title="Quit this session?"
        description="Cards you've already answered are saved. Cards you haven't seen yet won't count."
        confirmLabel="Quit session"
        cancelLabel="Keep going"
        destructive
        onConfirm={() => navigate(`/apps/${appId}`)}
      />
    </>
  );
}
